// <fiee-rich-text> is the small rich-text editor of the admin panel
// (view.richTextEditor). It wraps a contenteditable surface, a toolbar and the
// hidden textarea that actually posts the HTML.
//
// Without JavaScript the textarea stays visible (see admin.css), so the HTML
// can still be edited by hand. Once this island hydrates it hides the source
// and keeps it in sync on every input and before the form is sent. The server
// sanitises the HTML again with model.SanitizeHTML, so this editor is only a
// convenience, not the security boundary.
export class FieeRichText extends HTMLElement {
  #surface: HTMLElement | null = null;
  #source: HTMLTextAreaElement | null = null;
  #form: HTMLFormElement | null = null;
  #toolbar: HTMLElement | null = null;

  #onInput = () => this.#sync();
  #onSubmit = () => this.#sync();
  #onToolbarClick = (e: Event) => this.#command(e);
  #onToolbarMouseDown = (e: Event) => {
    // Keep the selection in the surface when a toolbar button is pressed.
    if ((e.target as Element).closest("button")) e.preventDefault();
  };

  connectedCallback() {
    this.#surface = this.querySelector<HTMLElement>("[data-rich-surface]");
    this.#source = this.querySelector<HTMLTextAreaElement>("[data-rich-source]");
    this.#toolbar = this.querySelector<HTMLElement>(".rich-text-editor__toolbar");
    if (!this.#surface || !this.#source) return;

    // The textarea is the value the server rendered; refresh the surface from
    // it so both start exactly the same.
    if (this.#surface.innerHTML.trim() !== this.#source.value.trim()) {
      this.#surface.innerHTML = this.#source.value;
    }

    this.#surface.addEventListener("input", this.#onInput);
    this.#toolbar?.addEventListener("click", this.#onToolbarClick);
    this.#toolbar?.addEventListener("mousedown", this.#onToolbarMouseDown);

    this.#form = this.closest("form");
    this.#form?.addEventListener("submit", this.#onSubmit);
  }

  disconnectedCallback() {
    this.#surface?.removeEventListener("input", this.#onInput);
    this.#toolbar?.removeEventListener("click", this.#onToolbarClick);
    this.#toolbar?.removeEventListener("mousedown", this.#onToolbarMouseDown);
    this.#form?.removeEventListener("submit", this.#onSubmit);
  }

  #sync() {
    if (!this.#surface || !this.#source) return;
    const html = this.#surface.innerHTML.trim();
    this.#source.value = html === "<br>" ? "" : html;
  }

  #command(e: Event) {
    const button = (e.target as Element).closest<HTMLButtonElement>(
      "[data-rich-cmd], [data-rich-block], [data-rich-link]",
    );
    if (!button || !this.#surface) return;
    e.preventDefault();
    this.#surface.focus();

    if (button.dataset["richLink"] !== undefined) {
      const url = window.prompt("URL del enlace (https://…)");
      if (!url) return;
      if (!/^https?:\/\//i.test(url.trim())) {
        window.alert("El enlace debe empezar por http:// o https://.");
        return;
      }
      document.execCommand("createLink", false, url.trim());
    } else if (button.dataset["richBlock"]) {
      document.execCommand("formatBlock", false, `<${button.dataset["richBlock"]}>`);
    } else if (button.dataset["richCmd"]) {
      document.execCommand(button.dataset["richCmd"], false);
    }

    this.#sync();
  }
}
